// React hook for email processing service
// Provides easy integration with the email processing functionality

import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { 
  processHistoricalEmails, 
  processDailyEmails, 
  retryEmailProcessing,
  getProcessingStatus,
  checkN8NWebhookHealth,
  refreshTokenIfNeeded
} from '@/services/emailProcessing';

export const useEmailProcessing = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [webhookHealth, setWebhookHealth] = useState(null);

  // Process historical emails
  const processHistorical = useCallback(async (userId) => {
    if (!userId) {
      setError('User ID is required');
      return null;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await processHistoricalEmails(supabase, userId);
      setLastResult(result);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to process historical emails';
      setError(errorMessage);
      console.error('Historical processing error:', err);
      return {
        success: false,
        message: errorMessage,
        error: err.code || 'UNKNOWN_ERROR'
      };
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Process daily emails
  const processDaily = useCallback(async (userId) => {
    if (!userId) {
      setError('User ID is required');
      return null;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await processDailyEmails(supabase, userId);
      setLastResult(result);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to process daily emails';
      setError(errorMessage);
      console.error('Daily processing error:', err);
      return {
        success: false,
        message: errorMessage,
        error: err.code || 'UNKNOWN_ERROR'
      };
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Retry processing
  const retryProcessing = useCallback(async (userId, processingType) => {
    if (!userId) {
      setError('User ID is required');
      return null;
    }

    if (!['historical', 'daily'].includes(processingType)) {
      setError('Invalid processing type');
      return null;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await retryEmailProcessing(supabase, userId, processingType);
      setLastResult(result);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to retry processing';
      setError(errorMessage);
      console.error('Retry processing error:', err);
      return {
        success: false,
        message: errorMessage,
        error: err.code || 'UNKNOWN_ERROR'
      };
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Get processing status
  const getStatus = useCallback(async (batchId) => {
    if (!batchId) {
      setError('Batch ID is required');
      return null;
    }

    try {
      const status = await getProcessingStatus(batchId);
      return status;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get processing status';
      setError(errorMessage);
      console.error('Status check error:', err);
      return null;
    }
  }, []);

  // Check webhook health
  const checkHealth = useCallback(async () => {
    try {
      const isHealthy = await checkN8NWebhookHealth();
      setWebhookHealth(isHealthy);
      return isHealthy;
    } catch (err) {
      console.error('Health check error:', err);
      setWebhookHealth(false);
      return false;
    }
  }, []);

  // Refresh token if needed
  const refreshToken = useCallback(async (userId) => {
    if (!userId) {
      setError('User ID is required');
      return false;
    }

    try {
      const refreshed = await refreshTokenIfNeeded(supabase, userId);
      return refreshed;
    } catch (err) {
      const errorMessage = err.message || 'Failed to refresh token';
      setError(errorMessage);
      console.error('Token refresh error:', err);
      return false;
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear last result
  const clearResult = useCallback(() => {
    setLastResult(null);
  }, []);

  // Reset all state
  const reset = useCallback(() => {
    setIsProcessing(false);
    setLastResult(null);
    setError(null);
    setWebhookHealth(null);
  }, []);

  return {
    // State
    isProcessing,
    lastResult,
    error,
    webhookHealth,
    
    // Actions
    processHistorical,
    processDaily,
    retryProcessing,
    getStatus,
    checkHealth,
    refreshToken,
    
    // Utilities
    clearError,
    clearResult,
    reset
  };
};
